import React, { useEffect, useMemo, useState } from 'react';
import { Reference } from '../StudyList';
import { useFilesForReference } from '../components/StudyReferenceFilesProvider';
import * as styles from './ReferenceActions.module.scss';
import { useStudyList } from './StudyListProvider';
import { LibkeyLink } from './LibkeyLink';
import {
  Badge,
  Button,
  Icon,
  IconList,
  KeywordHighlighter,
  Link,
  Text,
  LoadingSpinner,
} from 'components/core';
import { Inline, Stack } from 'components/layout';
import { hasValue } from 'util/hasValue';
import { useUniqueID } from 'hooks/useAccessibleName';

type ToggleButtonProps = {
  label: string;
  triggerId: string;
  isOpen?: boolean;
  onClick: () => void;
};

const ToggleButton = ({
  label,
  triggerId,
  isOpen,
  onClick,
  ...rest
}: ToggleButtonProps) => {
  const lowerCaseLabel = label.toLowerCase();
  const pendoButtonName = `${lowerCaseLabel.replace(/\s+/g, '-')}-button`;

  return (
    <Button
      aria-expanded={isOpen}
      aria-controls={triggerId}
      size="sm"
      variant="outline"
      type="brand"
      data-pendo-key={
        isOpen ? `hide-${pendoButtonName}` : `show-${pendoButtonName}`
      }
      onClick={onClick}
      {...rest}
    >
      {label}

      <Icon
        icon={isOpen ? IconList.light.faAngleUp : IconList.light.faAngleDown}
      />
    </Button>
  );
};

const useToggleIds = () => {
  const fullTextToggleId = useUniqueID({ postfix: 'full-text-toggle-button' });
  const abstractToggleId = useUniqueID({ postfix: 'abstract-toggle-button' });

  return useMemo(
    () => ({
      fullTextToggleId,
      abstractToggleId,
    }),
    [fullTextToggleId, abstractToggleId]
  );
};

export type ReferenceActionsProps = {
  reference: Pick<Reference, 'id' | 'abstract'>;
  documentUrl: string | null;
  showAbstracts: boolean;
  inclusionWords: string[];
  exclusionWords: string[];
  showFullTextFeatures?: boolean;
  showUploadedFiles?: boolean;
  setShowUploadModal: () => void;
};

export const ReferenceActions = ({
  reference,
  documentUrl,
  inclusionWords,
  exclusionWords,
  showAbstracts: showAbstractsProp,
  showFullTextFeatures = false,
  showUploadedFiles = false,
  setShowUploadModal,
}: ReferenceActionsProps) => {
  const { id: referenceId, abstract } = reference;
  const { fullTextToggleId, abstractToggleId } = useToggleIds();
  const [fullTextToggleIsOpen, setFullTextToggleIsOpen] = useState(
    showFullTextFeatures
  );
  const [abstractToggleIsOpen, setAbstractToggleIsOpen] = useState(false);
  const { isHighlightsEnabled } = useStudyList();

  const { files: pdfFiles } = useFilesForReference(referenceId);

  const hasAbstract = hasValue(abstract);
  const [hasFiles, setHasFiles] = useState(false);

  const hasDocumentUrl = documentUrl !== null && documentUrl !== '';
  const hasFilesOrUrl = hasFiles || hasDocumentUrl;
  const [showFullTextToggle, setShowFullTextToggle] = useState(
    showFullTextFeatures && hasFilesOrUrl
  );

  useEffect(() => {
    setShowFullTextToggle(showFullTextFeatures && hasFilesOrUrl);
  }, [showFullTextFeatures, hasFilesOrUrl]);

  useEffect(() => {
    if (showUploadedFiles) {
      setShowFullTextToggle(hasFiles || hasDocumentUrl);
    }
  }, [showUploadedFiles, hasFiles, hasDocumentUrl]);

  useEffect(() => {
    setHasFiles(pdfFiles.length > 0);
  }, [pdfFiles.length]);

  useEffect(() => {
    setAbstractToggleIsOpen(!!showAbstractsProp);
  }, [showAbstractsProp]);

  return (
    <>
      <div className={styles.actions}>
        {showFullTextFeatures && (
          <LibkeyLink
            referenceId={reference.id}
            className={styles.libkeyLink}
          />
        )}
        <Inline alignX="left" spacing={3}>
          <>
            {showFullTextFeatures && (
              <>
                {showFullTextToggle ? (
                  <ToggleButton
                    triggerId={fullTextToggleId}
                    isOpen={fullTextToggleIsOpen}
                    onClick={() => setFullTextToggleIsOpen((toggle) => !toggle)}
                    label="Full text"
                  />
                ) : (
                  <Button
                    size="sm"
                    type="brand"
                    onClick={setShowUploadModal}
                    data-pendo-key="upload-full-text-button"
                  >
                    <Icon icon={IconList.light.faFileArrowUp} />
                    Upload full text
                  </Button>
                )}
              </>
            )}
          </>

          {hasAbstract && (
            <ToggleButton
              triggerId={abstractToggleId}
              isOpen={abstractToggleIsOpen}
              onClick={() => {
                setAbstractToggleIsOpen((open) => !open);
              }}
              label="Abstract"
            />
          )}
        </Inline>
      </div>

      {abstractToggleIsOpen && hasAbstract && (
        <div id={abstractToggleId}>
          <Text className={styles.abstract}>
            <KeywordHighlighter
              text={abstract}
              inclusionWords={inclusionWords}
              exclusionWords={exclusionWords}
              isHighlightingEnabled={isHighlightsEnabled}
            />
          </Text>
        </div>
      )}

      {showFullTextFeatures && showFullTextToggle && fullTextToggleIsOpen && (
        <Stack
          id={fullTextToggleId}
          aria-live="polite"
          alignX="left"
          spacing={1}
          className={styles.stack}
        >
          {!hasFiles && !hasDocumentUrl && (
            <Inline alignX="left" spacing={1}>
              <LoadingSpinner type="primary" />
              <Text size="sm" component="span">
                Loading full text...
              </Text>
            </Inline>
          )}
          {(hasFiles || hasDocumentUrl) && (
            <>
              <Stack spacing={2} component="ul" className={styles.stack}>
                {pdfFiles
                  .filter((file) => file.uploadComplete)
                  .map((file) => {
                    return (
                      <li key={file.id}>
                        <Inline alignX="left" shouldWrap={false}>
                          <Link
                            variant="primary"
                            to={file.url as string}
                            className={styles.fullTextLink}
                            external
                            data-pendo-key="reference-pdf-link"
                          >
                            <span>{file.uploadFileName}</span>
                          </Link>

                          {file.isPrimary && (
                            <Badge variant="solid" type="primary">
                              Primary
                            </Badge>
                          )}
                        </Inline>

                        <Text variant="success">
                          Full text uploaded by {file.uploadedBy}
                        </Text>
                      </li>
                    );
                  })}
                {documentUrl && (
                  <li>
                    <Link
                      variant="primary"
                      to={documentUrl}
                      className={styles.fullTextLink}
                      external
                      data-pendo-key="reference-pdf-link"
                    >
                      <span>{documentUrl}</span>
                    </Link>
                  </li>
                )}
              </Stack>

              <Button
                variant="ghost"
                type="brand"
                size="sm"
                onClick={setShowUploadModal}
                data-pendo-key="manage-full-text-button"
              >
                <Icon icon={IconList.light.faClipboard} />
                Manage full text
              </Button>
            </>
          )}
        </Stack>
      )}
    </>
  );
};
